import { Button, Center, Group, Space, Stack } from '@mantine/core';
import { useMemo, useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AIModelType,
  createRobotDatasets,
  getDatasetsFilesType,
  LarkFileListParams,
  postLarkFileList,
  useDataSourcesModal,
} from '@mygpt/app';
import { showNotification } from '@mantine/notifications';
import { gbaseAuthorize, getGbaseAuthStatus, disconnectGbaseAuthorization } from '@mygpt/app/apis';

import { Card } from '@/components/Create_V2/components/Card';
import { Lark } from '@/components/Create_V2/components/Integrations/Lark';
import { GbaseProject } from '@/components/Create_V2/components/Integrations/GbaseProject';
import { ModelSelect } from '@/components/Create_V2/ModelSelect';
import { useCreateStore } from '@/store/create';
import { useGPTStore } from '@/store';
import { useIntegrationRuleStore } from '@/pages/Datasets/Integration/Tasks/useIntegrationRuleStore';
import { useDatasetStore } from '@/store/dataset';

export default () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { datasetId: routeDatasetId } = useParams<{ datasetId?: string }>();
  const { onClose, openGbaseModal, setCounter, counter } = useDataSourcesModal();

  const {
    botId,
    datasetId,
    creating,
    pageType,
    embeddingModelName,
    startCreating,
    closeCreating,
    setBotId,
    setDatasetId,
    setPageType,
  } = useCreateStore();
  const { fetchRules } = useIntegrationRuleStore();
  const { assetScope } = useGPTStore();
  const isCloudMode = import.meta.env.VITE_MODE !== 'local';

  const [fileType, setFileType] = useState<AIModelType>(AIModelType.LARK_FILE);
  const { init: initFilesInfo, setCount: setDatasetCount } = useDatasetStore();
  const [larkParams, setLarkParams] = useState<LarkFileListParams>({
    app_id: '',
    app_secret: '',
    share_urls: [],
    notification_emails: [],
    name: '',
    sync_interval: 1,
  });
  const [gbaseAuthorized, setGbaseAuthorized] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(false);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const createRobotDatasetTask = async () => {
    if (pageType !== 'create') {
      return {
        newBotId: botId,
        datasetId,
      };
    }

    const res = await createRobotDatasets({
      aiModel: AIModelType.FILE,
      name: 'Lark',
      embeddingModelName,
      ...(isCloudMode && assetScope && { companyId: assetScope }),
    });
    if (!res) return Promise.reject(t('create_v2.create_robot_fail'));

    const datasets = res.data;
    if (datasets.error) return Promise.reject(datasets.error);
    const bId = queryParams.get('botId');
    setBotId(botId || '');
    const { id: dId } = datasets;
    setDatasetId(dId);
    setPageType('modify');

    return {
      newBotId: bId,
      datasetId: dId,
    };
  };

  const freshDatasetCounts = async () => {
    if (!datasetId) return;
    try {
      const res = await getDatasetsFilesType({ id: datasetId, language: i18n.language });
      initFilesInfo(res.data);
    } catch (err) {
      console.error('获取知识库数据的数量数据失败：', err);
    }
  };

  /**
   * 1. （如果需要）创建机器人+数据集
   * 2. 提交导入三方数据源需要的配置信息给后端
   */
  const submit = async () => {
    startCreating();

    try {
      const { newBotId, datasetId } = await createRobotDatasetTask();

      if (fileType === AIModelType.LARK_FILE) {
        await postLarkFileList(datasetId, larkParams);
        fetchRules({ datasetId });
        // 如果同时调的话，后端接口返回的文件数量没有变化，实际上是因为后端也在调用集成的服务的接口，不一定能及时返回
        // 未来希望这种场景能用websocket来解决。这次前端先延迟5秒解决。
        setTimeout(() => {
          freshDatasetCounts();
        }, 5000);
      }

      if (pageType === 'create') {
        // 接口提交成功后，直接跳转到知识库的集成页面
        navigate(`/bot/${newBotId}/datasets/${datasetId}/data-source/integration`);
      } else {
        // 直接关闭弹框
        onClose();
      }
    } finally {
      closeCreating();
    }
  };

  const renderIntegrationForm = (fileType: AIModelType) => {
    switch (fileType) {
      case AIModelType.LARK_FILE:
        return <Lark onParamsChange={setLarkParams} />;
      case AIModelType.GBASE_PROJECT:
        return <GbaseProject authorized={gbaseAuthorized} loading={checkingAuth} />;
      default:
        return null;
    }
  };

  const isSubmitDisabled = useMemo(() => {
    if (fileType === AIModelType.LARK_FILE) {
      return !larkParams.app_id || !larkParams.app_secret || larkParams.share_urls.length === 0;
    }
    return false;
  }, [fileType, larkParams]);

  // 检查 GBase 授权状态
  const checkGbaseAuthStatus = async () => {
    if (!routeDatasetId) return;

    setCheckingAuth(true);
    try {
      const statusRes = await getGbaseAuthStatus(routeDatasetId);
      const { gbase_integration } = statusRes.data;
      setGbaseAuthorized(gbase_integration.authorized);
    } catch (error) {
      console.error('检查 GBase 授权状态失败:', error);
      setGbaseAuthorized(false);
    } finally {
      setCheckingAuth(false);
    }
  };

  // 组件初始化时就预先查询授权状态，避免点击时抖动
  useEffect(() => {
    if (routeDatasetId) {
      checkGbaseAuthStatus();
    }
  }, [routeDatasetId]);

  const handleGbaseConnect = async () => {
    if (!routeDatasetId) return;

    try {
      // 先检查授权状态
      const statusRes = await getGbaseAuthStatus(routeDatasetId);
      const { gbase_integration } = statusRes.data;

      // 如果已经授权，直接打开选择 project 的弹窗，并关闭原有弹窗
      if (gbase_integration.authorized) {
        openGbaseModal();
        onClose();
        return;
      }

      // 未授权，保存当前页面的相对路径（pathname + search），授权成功后跳回
      const redirectPath = window.location.pathname + window.location.search;
      localStorage.setItem('gbase_auth_redirect_url', redirectPath);

      const res = await gbaseAuthorize({
        dataset_id: routeDatasetId,
      });

      if (res.data.authorization_url) {
        // 直接跳转到授权 URL，后端返回的就是正确的地址
        window.location.href = res.data.authorization_url;
      }
    } catch (error) {
      console.error('发起 GBase 授权失败:', error);
    }
  };

  // 断开 GBase 授权连接
  const handleGbaseDisconnect = async () => {
    if (!routeDatasetId) return;

    try {
      await disconnectGbaseAuthorization(routeDatasetId);

      // 显示成功提示
      showNotification({
        message: t('gbase.disconnect_success'),
        color: 'green',
      });

      // 立即更新gbase_project计数为0
      setDatasetCount('gbase_project', 0);

      // 断开成功后刷新授权状态
      await checkGbaseAuthStatus();
      // 刷新集成列表数据
      await freshDatasetCounts();
      // 触发DataSources页面的数据刷新
      setCounter(counter + 1);
    } catch (error) {
      console.error('断开 GBase 授权失败:', error);

      // 显示失败提示
      showNotification({
        message: t('gbase.disconnect_error'),
        color: 'red',
      });
    }
  };

  const integrations = useMemo(() => {
    const baseIntegrations = [
      {
        label: t('create_v2.integrations.data_sources.lark.label'),
        desc: t('create_v2.integrations.data_sources.lark.desc'),
        // icon: <SvgLark />,
        type: AIModelType.LARK_FILE,
      },
    ];

    // 只有在知识库页面（有 datasetId）时才显示 GBase Project 选项
    if (routeDatasetId) {
      baseIntegrations.push({
        label: t('create_v2.integrations.data_sources.gbase_project.label'),
        desc: t('create_v2.integrations.data_sources.gbase_project.desc'),
        // icon: <SvgBox />,
        type: AIModelType.GBASE_PROJECT,
      });
    }

    return baseIntegrations;
  }, [t, routeDatasetId]);

  return (
    <section>
      <h3 className="gray.8 font-medium">{t('create_v2.integrations.label')}</h3>
      <Space h={10} />
      <Group>
        {integrations.map((item) => (
          <Card
            key={item.type}
            {...item}
            active={fileType === item.type}
            onClick={() => {
              setFileType(item.type);
            }}
          />
        ))}
      </Group>
      <Stack>
        {renderIntegrationForm(fileType)}
        {fileType !== AIModelType.GBASE_PROJECT && <ModelSelect />}
        <Center mt={10}>
          {fileType === AIModelType.GBASE_PROJECT ? (
            <Group>
              <Button size="md" onClick={handleGbaseConnect}>
                {gbaseAuthorized ? t('gbase.select_project_button') : t('gbase.start_connect_button')}
              </Button>
              {gbaseAuthorized && (
                <Button size="md" variant="outline" color="red" onClick={handleGbaseDisconnect}>
                  {t('gbase.disconnect_button')}
                </Button>
              )}
            </Group>
          ) : (
            <Button size="md" onClick={() => submit().then()} disabled={isSubmitDisabled} loading={creating}>
              {t('create_v2.start_training')}
            </Button>
          )}
        </Center>
      </Stack>
    </section>
  );
};
